import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  Card, Badge, Button, Spinner, Input,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@agion/ui'
import { ArrowLeft, Key, Play, Trash2, AlertTriangle, Package, Search, Terminal, Download, Activity, Wrench, Database } from 'lucide-react'
import { CloudProviderIcon } from './InfraCloudProviderIcon'
import { DeploymentModelBadge } from './InfraDeploymentModelBadge'
import { OperationStatusBadge } from '@/features/operation/InfraOperationStatusBadge'
import { ApplyTerraformDialog } from './InfraApplyTerraformDialog'
import { EdgeRuntimeDialog } from './InfraEdgeRuntimeDialog'
import { HostUpgradeDialog } from './HostUpgradeDialog'
import { FailedProvisionCleanup } from './InfraFailedProvisionCleanup'
import { formatOperationType } from './infra_operation_label'
import { ProvisioningLifecycle } from './InfraProvisioningLifecycle'
import { HostStatusPanel } from './InfraHostStatusPanel'
import { deriveLifecycleStages } from './infra_lifecycle_stages'
import { getEnvironment, deleteEnvironment } from './infra_environment.service'
import { InfraHelmOverridesCard } from './InfraHelmOverridesCard'
import { listSshKeys } from '@/features/secondary/infra_ssh_key.service'
import type { SshKey } from '@/features/secondary/infra_ssh_key.service'
import { listOperations, createOperation } from '@/features/operation/infra_operation.service'
import { getHostGroup, getAgentHost } from './infra_governance.service'
import type { CloudEnvironment, DeploymentModel, EnvironmentStatus, InfraOperation, HostGroupMember, OperationType } from '@/core/infra_types'
import { formatDate } from '@/core/infra_utils'

const HELM_DEPLOYMENT_MODELS: DeploymentModel[] = ['k3s_bare', 'k8s_edge', 'dc_ha']
function needsHelmDeploy(model: string): boolean {
  return (HELM_DEPLOYMENT_MODELS as string[]).includes(model)
}

const RUNTIME_LOG_OPERATION_TYPES: OperationType[] = [
  'helm_install',
  'helm_upgrade',
  'helm_reinstall',
  'helm_uninstall',
  'helm_rollback',
  'helm_status',
  'host_diagnostics',
]

const ACTIONABLE_STATUSES: EnvironmentStatus[] = ['active', 'failed', 'decommissioning']

interface HostLogTarget {
  hostId: string
  label: string
}

function latestOperationByTypes(
  operations: InfraOperation[],
  types: OperationType[],
): InfraOperation | undefined {
  return operations
    .filter(op => types.includes(op.operationType))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
}


function readConfigString(config: Record<string, unknown>, camelKey: string, snakeKey: string): string {
  const raw = config[camelKey] ?? config[snakeKey]
  return typeof raw === 'string' ? raw.trim() : ''
}

/** Read a config string honouring camelCase + snake_case shapes; non-string
 * primitives are coerced (numbers stringify; null/undefined return ''). */
function readConfigField(config: Record<string, unknown>, camelKey: string, snakeKey: string): string {
  const raw = config[camelKey] ?? config[snakeKey]
  if (raw == null || raw === '') return ''
  return String(raw)
}

/** Coerce a config value to a boolean, treating `'false'` strings as false
 * (Boolean('false') === true is the trap that already burned the apply
 * dialog — do not regress here). */
function readConfigBool(config: Record<string, unknown>, camelKey: string, snakeKey: string): boolean | null {
  const raw = config[camelKey] ?? config[snakeKey]
  if (raw == null || raw === '') return null
  if (typeof raw === 'boolean') return raw
  if (typeof raw === 'string') return raw === 'true'
  return Boolean(raw)
}

function pickHostLogTarget(
  config: Record<string, unknown>,
  members: HostGroupMember[],
): HostLogTarget | null {
  const candidate = members
    .filter(member => member.hostId.trim() !== '')
    .sort((a, b) => {
      if (a.hostStatus === 'online' && b.hostStatus !== 'online') return -1
      if (a.hostStatus !== 'online' && b.hostStatus === 'online') return 1
      return a.priority - b.priority
    })[0]

  if (candidate) {
    return {
      hostId: candidate.hostId,
      label: candidate.hostName ?? candidate.hostId,
    }
  }

  const singleHostId = readConfigString(config, 'hostId', 'host_id')
  if (singleHostId) {
    return { hostId: singleHostId, label: singleHostId }
  }

  return null
}

const statusVariantMap: Record<EnvironmentStatus, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  pending: 'default',
  provisioning: 'info',
  active: 'success',
  updating: 'info',
  failed: 'error',
  decommissioning: 'warning',
  decommissioned: 'default',
}

function ConfigField({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{label}</p>
      <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{value}</p>
    </div>
  )
}

export function EnvironmentDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation(['pages'])
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [environment, setEnvironment] = useState<CloudEnvironment | null>(null)
  const [operations, setOperations] = useState<InfraOperation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showApplyDialog, setShowApplyDialog] = useState(false)
  const [showEdgeDialog, setShowEdgeDialog] = useState(false)
  const [edgeDialogMode, setEdgeDialogMode] = useState<'upgrade' | 'reinstall' | 'uninstall'>('upgrade')
  const [showHostPanel, setShowHostPanel] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isCheckingStatus, setIsCheckingStatus] = useState(false)
  const [isRunningDiagnostics, setIsRunningDiagnostics] = useState(false)
  const [isCheckingDbLogins, setIsCheckingDbLogins] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [showDecommissionDialog, setShowDecommissionDialog] = useState(false)
  const [isDecommissioning, setIsDecommissioning] = useState(false)
  const [confirmName, setConfirmName] = useState('')
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false)
  const [hostGroupName, setHostGroupName] = useState<string | null>(null)
  const [hostGroupMemberCount, setHostGroupMemberCount] = useState(0)
  const [hostGroupMembers, setHostGroupMembers] = useState<HostGroupMember[]>([])
  const [sshKeys, setSshKeys] = useState<SshKey[]>([])

  const load = useCallback(async () => {
    if (!id) return
    try {
      setIsLoading(true)
      const [env, ops] = await Promise.all([
        getEnvironment(id),
        listOperations({ environmentId: id, perPage: 20 }),
      ])
      setEnvironment(env)
      setOperations(Array.isArray(ops.data) ? ops.data : [])
      const cfg = (env.config ?? {}) as Record<string, unknown>
      // Load SSH keys for k3s environments (for display)
      if (env.deploymentModel === 'k3s_bare') {
        listSshKeys().then(resp => setSshKeys(resp.data ?? [])).catch(() => {})
      }
      // Load host group and members if configured. Single-host envs carry
      // `hostId` instead of `hostGroupId`; synthesize a one-element members
      // array so the lifecycle bar's Connected stage and runtime panel work
      // identically for both shapes.
      const hgId = (cfg['hostGroupId'] ?? cfg['host_group_id']) as string | undefined
      const singleHostId = (cfg['hostId'] ?? cfg['host_id']) as string | undefined
      setHostGroupName(null)
      setHostGroupMemberCount(0)
      setHostGroupMembers([])
      if (hgId) {
        getHostGroup(hgId)
          .then(g => {
            setHostGroupName(g.name)
            const members = g.members ?? []
            setHostGroupMemberCount(members.length)
            setHostGroupMembers(members)
            if (import.meta.env.DEV) {
              console.log('[env-detail] host group loaded:', g.name, 'members:', members.length, 'online:', members.filter(m => m.hostStatus === 'online').length)
            }
          })
          .catch((err) => {
            if (import.meta.env.DEV) console.warn('[env-detail] host group load failed:', err)
            setHostGroupName(null)
            setHostGroupMemberCount(0)
            setHostGroupMembers([])
          })
      } else if (singleHostId) {
        getAgentHost(singleHostId)
          .then(h => {
            const synthetic: HostGroupMember = {
              hostGroupId: '',
              hostId: h.id,
              priority: 0,
              joinedAt: h.lastHeartbeatAt ?? '',
              hostName: h.name,
              hostStatus: h.status,
            }
            setHostGroupMemberCount(1)
            setHostGroupMembers([synthetic])
          })
          .catch((err) => {
            if (import.meta.env.DEV) console.warn('[env-detail] single host load failed:', err)
          })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load environment')
    } finally {
      setIsLoading(false)
    }
  }, [id])

  useEffect(() => { void load() }, [load])

  // Deep-link: ?action=install-edge auto-opens the install dialog (from Charts Upgrade CTA)
  useEffect(() => {
    if (environment && searchParams.get('action') === 'install-edge' && needsHelmDeploy(environment.deploymentModel)) {
      setEdgeDialogMode('upgrade')
      setShowEdgeDialog(true)
      searchParams.delete('action')
      setSearchParams(searchParams, { replace: true })
    }
  }, [environment, searchParams, setSearchParams])

  if (isLoading) {
    return (
      <div className="container-standard flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  if (error || !environment) {
    return (
      <div className="container-standard">
        <Card>
          <div className="p-8 text-center text-red-600">
            {error ?? t('pages:environments.notFound', 'Environment not found')}
          </div>
        </Card>
      </div>
    )
  }

  const cfg = (environment.config ?? {}) as Record<string, unknown>
  const applyConfig = (cfg['vmApplyConfiguration'] ?? cfg['vm_apply_configuration'] ?? {}) as Record<string, unknown>
  const databaseDisk = readConfigField(cfg, 'dbDiskSizeGb', 'db_disk_size_gb')
    || readConfigField(applyConfig, 'dbDiskSizeGb', 'db_disk_size_gb')
  const currentKeyId = String(cfg['sshKeyId'] ?? cfg['ssh_key_id'] ?? '')
  const currentKey = sshKeys.find(k => k.id === currentKeyId)
  const sshKeyDisplay = currentKey
    ? `${currentKey.name} (${currentKey.keyType})`
    : (currentKeyId ? currentKeyId : t('pages:environments.detail.sshKeyPlaceholder', '\u2014'))
  const hgId = (cfg['hostGroupId'] ?? cfg['host_group_id']) as string | undefined
  const supportsRuntime = needsHelmDeploy(environment.deploymentModel)
  const isActionable = ACTIONABLE_STATUSES.includes(environment.status)
  const hasRuntimeControls = isActionable && supportsRuntime
  const hostLogTarget = pickHostLogTarget(cfg, hostGroupMembers)
  const onlineHostCount = hostGroupMembers.filter(member => member.hostStatus === 'online').length
  const runtimeLogOperation = supportsRuntime ? latestOperationByTypes(operations, RUNTIME_LOG_OPERATION_TYPES) : undefined
  const failedRuntimeOperation = supportsRuntime
    ? latestOperationByTypes(
      operations.filter(op => op.status === 'failed'),
      RUNTIME_LOG_OPERATION_TYPES,
    )
    : undefined
  const runtimeInProgress = runtimeLogOperation?.status === 'running' || runtimeLogOperation?.status === 'pending'
  const runtimeState = (() => {
    if (environment.status === 'failed' || failedRuntimeOperation) {
      return {
        variant: 'error' as const,
        label: t('pages:environments.runtime.stateNeedsAttention', 'Needs attention'),
        summary: t(
          'pages:environments.runtime.summaryFailed',
          'Start with diagnostics or the latest runtime operation logs before reinstalling.'
        ),
      }
    }
    if (runtimeInProgress) {
      return {
        variant: 'info' as const,
        label: t('pages:environments.runtime.stateRunning', 'Operation running'),
        summary: t(
          'pages:environments.runtime.summaryRunning',
          'A runtime operation is still running. Operation logs are the source of truth.'
        ),
      }
    }
    if (onlineHostCount > 0) {
      return {
        variant: 'success' as const,
        label: t('pages:environments.runtime.stateConnected', 'Connected'),
        summary: t(
          'pages:environments.runtime.summaryConnected',
          'The edge host is registered. Runtime output is still tracked through operations and diagnostics.'
        ),
      }
    }
    if (runtimeLogOperation) {
      return {
        variant: 'warning' as const,
        label: t('pages:environments.runtime.stateWaitingForHost', 'Waiting for host'),
        summary: t(
          'pages:environments.runtime.summaryWaitingForHost',
          'No compatible host log target is registered yet. Use runtime logs or diagnostics.'
        ),
      }
    }
    return {
      variant: 'default' as const,
      label: t('pages:environments.runtime.stateNotInstalled', 'Not installed'),
      summary: t(
        'pages:environments.runtime.summaryNotInstalled',
        'Install Edge Runtime after infrastructure is ready.'
      ),
    }
  })()

  async function startDiagnostics() {
    if (!environment) return
    setActionError(null)
    setIsRunningDiagnostics(true)
    try {
      const op = await createOperation({
        environmentId: environment.id,
        operationType: 'host_diagnostics',
        parameters: {},
      })
      navigate(`/operations/${op.id}`)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('pages:environments.actions.startFailed', 'Failed to start operation'))
    } finally {
      setIsRunningDiagnostics(false)
    }
  }

  async function startDbLoginsCheck() {
    if (!environment) return
    setActionError(null)
    setIsCheckingDbLogins(true)
    try {
      const op = await createOperation({
        environmentId: environment.id,
        operationType: 'host_diagnostics',
        parameters: { profile: 'db_logins' },
      })
      navigate(`/operations/${op.id}`)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('pages:environments.actions.startFailed', 'Failed to start operation'))
    } finally {
      setIsCheckingDbLogins(false)
    }
  }

  async function checkRuntimeStatus() {
    if (!environment) return
    setActionError(null)
    setIsCheckingStatus(true)
    try {
      const op = await createOperation({
        environmentId: environment.id,
        operationType: 'helm_status',
        parameters: { release_name: 'agion-edge' },
      })
      navigate(`/operations/${op.id}`)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('pages:environments.actions.startFailed', 'Failed to start operation'))
    } finally {
      setIsCheckingStatus(false)
    }
  }

  return (
    <div className="container-standard">
      {/* Back button */}
      <Button variant="ghost" size="sm" onClick={() => navigate('/environments')} className="mb-4">
        <ArrowLeft size={16} className="mr-1" />
        {t('pages:environments.backToList', 'Back to Environments')}
      </Button>

      {/* Environment Header */}
      <Card className="mb-6">
        <div className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <CloudProviderIcon provider={environment.cloudProvider} size={32} />
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  {environment.name}
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {/* eslint-disable-next-line i18next/no-literal-string -- pipe separator */}
                  {environment.region} | {t('pages:environments.createdBy', 'Created by')} {environment.createdBy}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={statusVariantMap[environment.status] ?? 'default'}>
                {t(`pages:environments.status.${environment.status}`, environment.status)}
              </Badge>
            </div>
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
            <ConfigField label={t('pages:environments.detail.provider', 'Cloud Provider')} value={environment.cloudProvider === 'none' ? t('pages:providers.onPremise') : environment.cloudProvider.toUpperCase()} />
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t('pages:environments.detail.model', 'Deployment Model')}</p>
              <DeploymentModelBadge model={environment.deploymentModel} />
            </div>
            <ConfigField label={t('pages:environments.detail.created', 'Created')} value={formatDate(environment.createdAt)} />
            <ConfigField label={t('pages:environments.detail.updated', 'Last Updated')} value={formatDate(environment.updatedAt)} />
          </div>

          {/* Configuration Section — universal, fields vary by deployment model */}
          <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">
              {t('pages:environments.detail.configuration', 'Configuration')}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {environment.deploymentModel === 'k3s_bare' && (
                <>
                  {cfg['bastionIp'] && <ConfigField label={t('pages:environments.detail.bastionIp', 'Bastion IP')} value={String(cfg['bastionIp'])} />}
                  {cfg['sshUser'] && <ConfigField label={t('pages:environments.detail.sshUser', 'SSH User')} value={String(cfg['sshUser'])} />}
                  {cfg['clusterMode'] && <ConfigField label={t('pages:environments.detail.clusterMode', 'Cluster Mode')} value={String(cfg['clusterMode']).toUpperCase()} />}
                  {Array.isArray(cfg['nodes']) && <ConfigField label={t('pages:environments.detail.nodes', 'Nodes')} value={String((cfg['nodes'] as unknown[]).length)} />}
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 mb-1">
                      <Key size={12} />
                      {t('pages:environments.detail.sshKey', 'SSH Key')}
                    </p>
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{sshKeyDisplay}</p>
                  </div>
                </>
              )}
              {(environment.deploymentModel === 'vm_ha' || environment.deploymentModel === 'vm_single') && (
                <>
                  {cfg['vmSize'] && <ConfigField label={t('pages:environments.detail.vmSize', 'VM Size')} value={String(cfg['vmSize'])} />}
                  {cfg['replicaCount'] != null && <ConfigField label={t('pages:environments.detail.replicas', 'Replicas')} value={String(cfg['replicaCount'])} />}
                  {readConfigField(cfg, 'zone', 'zone') && (
                    <ConfigField label={t('pages:environments.detail.zone', 'Zone')} value={readConfigField(cfg, 'zone', 'zone')} />
                  )}
                  {cfg['networkCidr'] && <ConfigField label={t('pages:environments.detail.network', 'Network')} value={String(cfg['networkCidr'])} />}
                  {cfg['haMode'] && <ConfigField label={t('pages:environments.detail.haMode', 'HA Mode')} value={String(cfg['haMode'])} />}
                  {readConfigField(cfg, 'diskSizeGb', 'disk_size_gb') && (
                    <ConfigField
                      label={t('pages:environments.detail.diskSize', 'Boot disk (GB)')}
                      value={readConfigField(cfg, 'diskSizeGb', 'disk_size_gb')}
                    />
                  )}
                  {readConfigBool(cfg, 'bastionEnabled', 'bastion_enabled') !== null && (
                    <ConfigField
                      label={t('pages:environments.detail.bastion', 'Bastion')}
                      value={
                        readConfigBool(cfg, 'bastionEnabled', 'bastion_enabled')
                          ? t('pages:environments.detail.bastionEnabled', 'Enabled')
                          : t('pages:environments.detail.bastionDisabled', 'Disabled')
                      }
                    />
                  )}
                  {readConfigBool(cfg, 'dfsEnabled', 'dfs_enabled') !== null && (
                    <ConfigField
                      label={t('pages:environments.detail.dfs', 'Durable file storage')}
                      value={
                        readConfigBool(cfg, 'dfsEnabled', 'dfs_enabled')
                          ? t('pages:environments.detail.dfsEnabled', 'Enabled')
                          : t('pages:environments.detail.dfsDisabled', 'Disabled')
                      }
                    />
                  )}
                  {readConfigBool(cfg, 'managedDbEnabled', 'managed_db_enabled') !== null && (
                    <ConfigField
                      label={t('pages:environments.detail.managedDb', 'Managed database')}
                      value={
                        readConfigBool(cfg, 'managedDbEnabled', 'managed_db_enabled')
                          ? t('pages:environments.detail.managedDbEnabled', 'Enabled')
                          : t('pages:environments.detail.managedDbDisabled', 'Disabled')
                      }
                    />
                  )}
                  {readConfigBool(cfg, 'managedDbEnabled', 'managed_db_enabled') && readConfigField(cfg, 'dbTier', 'db_tier') && (
                    <ConfigField
                      label={t('pages:environments.detail.dbTier', 'Database tier')}
                      value={readConfigField(cfg, 'dbTier', 'db_tier')}
                    />
                  )}
                  {environment.cloudProvider === 'gcp' && readConfigBool(cfg, 'managedDbEnabled', 'managed_db_enabled') !== false && databaseDisk && (
                    <ConfigField label={t('pages:vmWizard.databaseDisk')} value={databaseDisk} />
                  )}
                  {readConfigBool(cfg, 'separateIntelligenceDb', 'separate_intelligence_db') === true && (
                    <ConfigField
                      label={t('pages:environments.detail.separateIntelligenceDb', 'Separate intelligence DB')}
                      value={t('pages:environments.detail.dfsEnabled', 'Enabled')}
                    />
                  )}
                  {readConfigBool(cfg, 'separateIntelligenceDb', 'separate_intelligence_db') === true
                    && readConfigField(cfg, 'intelligenceDbTier', 'intelligence_db_tier') && (
                    <ConfigField
                      label={t('pages:environments.detail.intelligenceDbTier', 'Intelligence DB tier')}
                      value={readConfigField(cfg, 'intelligenceDbTier', 'intelligence_db_tier')}
                    />
                  )}
                  {readConfigField(cfg, 'tfModuleVersion', 'tf_module_version') && (
                    <ConfigField
                      label={t('pages:environments.detail.tfModuleVersion', 'Module version')}
                      value={readConfigField(cfg, 'tfModuleVersion', 'tf_module_version')}
                    />
                  )}
                  {(readConfigField(cfg, 'currentImage', 'current_image')
                    || readConfigField(cfg, 'packerImageVersion', 'packer_image_version')) && (
                    <ConfigField
                      label={t('pages:environments.detail.packerImage', 'Edge image')}
                      value={
                        readConfigField(cfg, 'currentImage', 'current_image')
                          || readConfigField(cfg, 'packerImageVersion', 'packer_image_version')
                      }
                    />
                  )}
                </>
              )}
              {environment.deploymentModel === 'k8s_edge' && (
                <>
                  <ConfigField label={t('pages:environments.detail.region', 'Region')} value={environment.region} />
                </>
              )}
              {hgId && hostGroupName && (
                <ConfigField label={t('pages:environments.detail.hostGroup', 'Host Group')} value={hostGroupName} />
              )}
              {/* Remote endpoint IP — shown whenever init_server_ip is configured
                  (the IP the platform SSHs to for host_upgrade and other host ops). */}
              {cfg['initServerIp'] != null && cfg['initServerIp'] !== '' && (
                <ConfigField
                  label={t('pages:environments.detail.remoteEndpoint', 'Remote Endpoint IP')}
                  value={String(cfg['initServerIp'])}
                />
              )}
              {cfg['init_server_ip'] != null && cfg['init_server_ip'] !== '' && cfg['initServerIp'] == null && (
                <ConfigField
                  label={t('pages:environments.detail.remoteEndpoint', 'Remote Endpoint IP')}
                  value={String(cfg['init_server_ip'])}
                />
              )}
            </div>
          </div>

          {/* Provisioning Lifecycle */}
          <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
            <ProvisioningLifecycle
              stages={deriveLifecycleStages(environment, operations, hostGroupMembers)}
              onStageClick={(opId) => navigate(`/operations/${opId}`)}
              onConnectedClick={() => setShowHostPanel(true)}
            />
          </div>

          {/* Helm Overrides — durable env.Config.helm_overrides editor */}
          {supportsRuntime && (
            <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
              <InfraHelmOverridesCard environment={environment} onUpdated={load} />
            </div>
          )}

          {/* Actions */}
          <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
            {supportsRuntime && (
              <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Package size={16} className="text-slate-500 dark:text-slate-400" />
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {t('pages:environments.runtime.title', 'Edge Runtime')}
                      </h3>
                      <Badge variant={runtimeState.variant}>{runtimeState.label}</Badge>
                    </div>
                    <p className="mt-2 max-w-3xl text-sm text-slate-600 dark:text-slate-400">
                      {runtimeState.summary}
                    </p>
                  </div>

                  <div className="grid gap-2 sm:grid-cols-3 lg:min-w-[420px]">
                    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {t('pages:environments.runtime.hostSignal', 'Host')}
                      </p>
                      <p className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                        {hostGroupMembers.length > 0
                          ? t('pages:environments.runtime.hostsOnline', '{{online}} / {{total}} online', {
                            online: onlineHostCount,
                            total: hostGroupMembers.length,
                          })
                          : t('pages:environments.runtime.noHostTarget', 'No host target')}
                      </p>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {t('pages:environments.runtime.runtimeLogsSignal', 'Runtime logs')}
                      </p>
                      <p className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                        {runtimeLogOperation
                          ? t('pages:environments.runtime.latestOperation', '{{type}} - {{status}}', {
                            type: formatOperationType(runtimeLogOperation.operationType),
                            status: runtimeLogOperation.status,
                          })
                          : t('pages:environments.runtime.noRuntimeOperation', 'No operation yet')}
                      </p>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {t('pages:environments.runtime.liveLogsSignal', 'Live host logs')}
                      </p>
                      <p className="mt-1 truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                        {hostLogTarget
                          ? t('pages:environments.runtime.wired', 'Wired')
                          : t('pages:environments.runtime.unwired', 'Not wired')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {actionError && (
              <div
                role="alert"
                className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
              >
                {actionError}
              </div>
            )}

            <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_auto]">
              <div className={supportsRuntime ? 'grid gap-3 md:grid-cols-3' : 'grid gap-3 md:grid-cols-2'}>
                {supportsRuntime && (
                  <div className="min-w-0">
                    <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">
                      {t('pages:environments.actions.runtimeGroup', 'Runtime actions')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {runtimeLogOperation && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/operations/${runtimeLogOperation.id}`)}
                        >
                          <Activity size={14} />
                          {t('pages:environments.actions.runtimeLogs', 'Runtime Logs')}
                        </Button>
                      )}
                      {hasRuntimeControls && (
                        <>
                          <Button
                            variant={environment.status === 'failed' ? 'primary' : 'outline'}
                            size="sm"
                            disabled={isRunningDiagnostics}
                            onClick={() => void startDiagnostics()}
                          >
                            {isRunningDiagnostics ? <Spinner className="h-3.5 w-3.5" /> : <Wrench size={14} />}
                            {t('pages:environments.actions.diagnostics', 'Diagnostics')}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setActionError(null)
                              setEdgeDialogMode('upgrade')
                              setShowEdgeDialog(true)
                            }}
                          >
                            <Package size={14} />
                            {t('pages:environments.actions.edgeRuntime', 'Edge Runtime')}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={isCheckingStatus}
                            onClick={() => void checkRuntimeStatus()}
                          >
                            {isCheckingStatus ? <Spinner className="h-3.5 w-3.5" /> : <Search size={14} />}
                            {t('pages:environments.actions.checkStatus', 'Check Status')}
                          </Button>
                        </>
                      )}
                      {!runtimeLogOperation && !hasRuntimeControls && (
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {t('pages:environments.actions.runtimeUnavailable', 'Runtime actions appear when the environment is active or failed.')}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="min-w-0">
                  <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">
                    {t('pages:environments.actions.hostGroup', 'Host')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!hostLogTarget}
                      onClick={() => {
                        if (hostLogTarget) navigate(`/agent-hosts/${hostLogTarget.hostId}/logs`)
                      }}
                    >
                      <Terminal size={14} />
                      {t('pages:environments.actions.hostLogs', 'Host Logs')}
                    </Button>
                    {isActionable && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowUpgradeDialog(true)}
                      >
                        <Download size={14} />
                        {t('pages:environments.actions.upgradePackages', 'Upgrade Packages')}
                      </Button>
                    )}
                    {isActionable && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isCheckingDbLogins}
                        onClick={() => void startDbLoginsCheck()}
                      >
                        {isCheckingDbLogins ? <Spinner className="h-3.5 w-3.5" /> : <Database size={14} />}
                        {t('pages:environments.actions.checkDbLogins', 'Check database logins (V81)')}
                      </Button>
                    )}
                    {!hostLogTarget && (
                      <p className="basis-full text-xs text-slate-500 dark:text-slate-400">
                        {t('pages:environments.actions.hostLogsUnwired', 'No compatible agent-host target is registered for live host logs.')}
                      </p>
                    )}
                    {hostLogTarget && supportsRuntime && (
                      <p className="basis-full text-xs text-slate-500 dark:text-slate-400">
                        {t('pages:environments.actions.hostLogsScope', 'Host Logs streams the registered host. Use Runtime Logs or Diagnostics for Helm and pod output.')}
                      </p>
                    )}
                  </div>
                </div>

                <div className="min-w-0">
                  <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">
                    {t('pages:environments.actions.infrastructureGroup', 'Terraform')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setActionError(null)
                        setShowApplyDialog(true)
                      }}
                    >
                      <Play size={14} />
                      {t('pages:environments.actions.applyTerraform', 'Apply Terraform')}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-end justify-start gap-2 xl:justify-end">
                <FailedProvisionCleanup key={`${environment.organizationId}/${environment.id}`} environment={environment} onOperation={operationId => navigate(`/operations/${operationId}`)} />
                {isActionable && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      setConfirmName('')
                      setShowDecommissionDialog(true)
                    }}
                  >
                    <AlertTriangle size={14} className="mr-1" />
                    {t('pages:environments.actions.decommission', 'Decommission')}
                  </Button>
                )}
                {environment.status === 'decommissioned' && (
                  <Button variant="destructive" size="sm" onClick={() => setShowDeleteDialog(true)}>
                    <Trash2 size={14} className="mr-1" />
                    {t('pages:environments.actions.delete', 'Delete')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Recent Operations */}
      <div className="section-header">
        <h2 className="section-title">
          {t('pages:environments.detail.recentOps', 'Recent Operations')}
        </h2>
      </div>

      {operations.length === 0 ? (
        <Card>
          <div className="p-6 text-center text-slate-500 dark:text-slate-400">
            {t('pages:environments.detail.noOps', 'No operations recorded for this environment.')}
          </div>
        </Card>
      ) : (
        <Table variant="modern">
          <TableHeader variant="modern">
            <TableRow variant="modern">
              <TableHead variant="modern">{t('pages:operations.columns.status', 'Status')}</TableHead>
              <TableHead variant="modern">{t('pages:operations.columns.type', 'Operation')}</TableHead>
              <TableHead variant="modern">{t('pages:operations.columns.created', 'Created')}</TableHead>
              <TableHead variant="modern">{t('pages:operations.columns.createdBy', 'Created by')}</TableHead>
              <TableHead variant="modern">{t('pages:operations.columns.error', 'Error')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody variant="modern">
            {operations.map(op => (
              <TableRow
                key={op.id}
                variant="modern"
                className="cursor-pointer"
                onClick={() => navigate(`/operations/${op.id}`)}
              >
                <TableCell><OperationStatusBadge status={op.status} /></TableCell>
                <TableCell className="font-medium">{op.operationType}</TableCell>
                <TableCell className="text-slate-500 text-xs">{formatDate(op.createdAt)}</TableCell>
                <TableCell className="text-slate-500">{op.createdBy}</TableCell>
                <TableCell className="text-red-500 text-xs max-w-xs truncate">{op.errorMessage ?? ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <HostStatusPanel
        open={showHostPanel}
        onClose={() => setShowHostPanel(false)}
        members={hostGroupMembers}
      />

      {environment && (
        <>
          <ApplyTerraformDialog
            open={showApplyDialog}
            environment={environment}
            onClose={() => setShowApplyDialog(false)}
            onSuccess={(opId) => navigate(`/operations/${opId}`)}
          />
          <EdgeRuntimeDialog
            open={showEdgeDialog}
            environment={environment}
            onClose={() => setShowEdgeDialog(false)}
            onSuccess={(opId) => navigate(`/operations/${opId}`)}
            initialMode={edgeDialogMode}
          />
        </>
      )}

      {/* Decommission Dialog with Type-to-Confirm */}
      <Dialog open={showDecommissionDialog} onOpenChange={(isOpen) => { if (!isOpen) setShowDecommissionDialog(false) }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('pages:environments.decommissionDialog.title', 'Decommission Environment')}</DialogTitle>
            <DialogDescription>
              {t('pages:environments.decommissionDialog.description', 'This will permanently destroy all cloud resources, delete agent hosts, and remove the host group. This action cannot be undone.')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-3 space-y-3 text-sm">
            <ul className="list-disc ml-5 text-slate-600 dark:text-slate-400 space-y-1">
              <li>{t('pages:environments.decommissionDialog.terraformDestroy', 'Terraform destroy (cloud resources)')}</li>
              {hostGroupName && (
                <>
                  <li>{t('pages:environments.decommissionDialog.hostGroup', 'Host group: {{name}}', { name: hostGroupName })}</li>
                  <li>{t('pages:environments.decommissionDialog.hosts', '{{count}} agent host(s)', { count: hostGroupMemberCount })}</li>
                </>
              )}
              <li>{t('pages:environments.decommissionDialog.vpnGateway', 'VPN gateway (if configured)')}</li>
            </ul>
            <div>
              <p className="text-sm text-slate-700 dark:text-slate-300 mb-2">
                {t('pages:environments.decommissionDialog.typeToConfirm', 'Type "{{name}}" to confirm:', { name: environment.name })}
              </p>
              <Input
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                placeholder={t('pages:environments.decommissionDialog.confirmPlaceholder', 'Environment name')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDecommissionDialog(false)} disabled={isDecommissioning}>
              {t('pages:environments.decommissionDialog.cancel', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={isDecommissioning || confirmName !== environment.name}
              onClick={() => {
                if (!id || !environment) return
                setIsDecommissioning(true)
                void createOperation({
                  environmentId: id,
                  operationType: 'decommission',
                  parameters: {
                    module: cfg['module'] as string
                      ?? (environment.deploymentModel === 'k3s_bare' || environment.cloudProvider === 'none' ? 'k3s-cluster' : `${environment.cloudProvider}-vm`),
                    workspace: cfg['workspace'] as string ?? 'default',
                    variables: cfg['variables'] ?? {},
                  },
                })
                  .then((op) => navigate(`/operations/${op.id}`))
                  .catch((err) => {
                    setError(err instanceof Error ? err.message : 'Failed to start decommission')
                    setShowDecommissionDialog(false)
                  })
                  .finally(() => setIsDecommissioning(false))
              }}
            >
              {isDecommissioning ? <Spinner className="h-4 w-4 mr-1" /> : <AlertTriangle size={14} className="mr-1" />}
              {t('pages:environments.decommissionDialog.confirm', 'Decommission')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(isOpen) => { if (!isOpen) setShowDeleteDialog(false) }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('pages:environments.deleteDialog.title', 'Delete Environment')}</DialogTitle>
            <DialogDescription>
              {t('pages:environments.deleteDialog.description', 'This will permanently remove the environment record. Cloud resources have already been destroyed by the decommission operation.')}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm font-medium py-2">{environment?.name}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={isDeleting}>
              {t('pages:environments.deleteDialog.cancel', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={isDeleting}
              onClick={() => {
                if (!id) return
                setIsDeleting(true)
                void deleteEnvironment(id)
                  .then(() => navigate('/environments'))
                  .catch((err) => {
                    setError(err instanceof Error ? err.message : 'Failed to delete')
                    setShowDeleteDialog(false)
                  })
                  .finally(() => setIsDeleting(false))
              }}
            >
              {isDeleting ? <Spinner className="h-4 w-4 mr-1" /> : <Trash2 size={14} className="mr-1" />}
              {t('pages:environments.deleteDialog.confirm', 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Host Upgrade Dialog */}
      <HostUpgradeDialog
        open={showUpgradeDialog}
        environmentId={environment.id}
        onClose={() => setShowUpgradeDialog(false)}
        onSuccess={(operationId) => navigate(`/operations/${operationId}`)}
      />
    </div>
  )
}
